import moment from 'moment';

import { RateCorrectionValidationResult } from '../service/RateCorrectionsEditorHelpers/RateCorrectionsEditorHelpers';
import { RelevantPhrasesErrors } from '../service/RelevantPhrasesHelpers/RelevantPhrasesHelpers';

import { ChatListValue } from './chatList';
import {
    ErrorsForValidationErrorsData,
    PageStatus,
    ValidationErrorData
} from './common';
import { CampaignType } from './domain';
import {
    ExperimentsEditorValidationData,
    ExperimentsValue
} from './experimentsEditor';
import { Goal } from './goals';
import { GdBidModifierType } from './graphql-grid';
import { GdContentLanguage } from './graphql-grid';
import {
    MetrikaKeyGoalsValue,
    MetrikaKeyGoalValidationData
} from './metrikaKeyGoals';
import { MinusPhrasesValue } from './minusPhrases';
import {
    NotificationsEditorValidationData,
    NotificationsValue
} from './notificationEditor';
import {
    RateCorrectionType,
    RatesCorrectionsConstantsData,
    RatesCorrectionsValue,
    RetargetingConditionDataForRateCorrections
} from './ratesCorrections';
import { RelevantPhrasesValue } from './relevantPhrases';
import { Strategy, StrategyEditorValidationResult } from './strategy';
import { TimeTargetingValue } from './timeTargetingEditor';
import { VisitingCardValueEditCampaign } from './VisitingCardEditor';

export interface DataForEditCampaigns {
    campaigns: Record<string, EditCampaignData>;
    constants: EditCampaignsConstants;
}

export enum EditCampaignValueField {
    name = 'name',
    dates = 'dates',
    chatOperator = 'chatOperator',
    metrikaKeyGoals = 'metrikaKeyGoals',
    bannedDomains = 'bannedDomains',
    relevantPhrases = 'relevantPhrases',
    hasBannersTextSubstitution = 'hasBannersTextSubstitution',
    showYandexCatalogInfo = 'showYandexCatalogInfo',
    considerCompetitors = 'considerCompetitors',
    allowedPageIds = 'allowedPageIds',
    bannersLanguage = 'bannersLanguage',
    disabledIps = 'disabledIps',
    strategy = 'strategy',
    notifications = 'notifications',
    metrikaCounters = 'metrikaCounters',
    markMetrikaLinks = 'markMetrikaLinks',
    minusPhrases = 'minusPhrases',
    markTargetCallLinks = 'markTargetCallLinks',
    experiments = 'experiments',
    rateCorrections = 'rateCorrections',
    siteMonitoring = 'siteMonitoring',
    timeTargeting = 'timeTargeting',
    hasExtendedGeoTargeting = 'hasExtendedGeoTargeting',
    visitingCard = 'visitingCard'
}

export enum EditCampaignEditor {
    relevantPhrasesEditor = 'relevantPhrasesEditor',
    datesIntervalEditor = 'datesIntervalEditor',
    nameEditor = 'editPageItemNameEditor',
    disabledIpsEditor = 'disabledIpsEditor',
    allowedPageIdsEditor = 'allowedPageIdsEditor',
    bannedDomainsEditor = 'bannedDomainsEditor',
    notificationsEditor = 'notificationsEditor',
    metrikaCounterEditor = 'metrikaCounterEditor',
    experimentsEditor = 'experimentsEditor',
    rateCorrectionsEditor = 'rateCorrectionsEditor',
    strategyEditor = 'strategyEditor',
    timeTargetingEditor = 'timeTargetingEditor',
    metrikaKeyGoalsEditor = 'metrikaKeyGoalsEditor',
    bannersLanguage = 'bannersLanguage',
    visitingCardEditor = 'visitingCardEditor'
}

export interface EditCampaignDataValue {
    [EditCampaignValueField.name]: string;
    [EditCampaignValueField.dates]: {
        startDate: moment.Moment;
        endDate?: moment.Moment;
    };
    [EditCampaignValueField.chatOperator]: ChatListValue;
    [EditCampaignValueField.bannedDomains]?: string[];
    [EditCampaignValueField.disabledIps]: string[];
    [EditCampaignValueField.relevantPhrases]: RelevantPhrasesValue;
    [EditCampaignValueField.hasBannersTextSubstitution]: boolean;
    [EditCampaignValueField.showYandexCatalogInfo]: boolean;
    [EditCampaignValueField.considerCompetitors]: boolean;
    [EditCampaignValueField.allowedPageIds]?: string[];
    [EditCampaignValueField.bannersLanguage]?: GdContentLanguage;
    [EditCampaignValueField.notifications]: NotificationsValue;
    [EditCampaignValueField.strategy]: Strategy;
    [EditCampaignValueField.markMetrikaLinks]: boolean;
    [EditCampaignValueField.metrikaCounters]: string[];
    [EditCampaignValueField.experiments]: ExperimentsValue;
    [EditCampaignValueField.metrikaKeyGoals]: MetrikaKeyGoalsValue;
    [EditCampaignValueField.siteMonitoring]: boolean;
    [EditCampaignValueField.minusPhrases]: MinusPhrasesValue;
    [EditCampaignValueField.timeTargeting]: TimeTargetingValue;
    [EditCampaignValueField.rateCorrections]: RatesCorrectionsValue;
    [EditCampaignValueField.markTargetCallLinks]: boolean;
    [EditCampaignValueField.hasExtendedGeoTargeting]: boolean;
    [EditCampaignValueField.visitingCard]: VisitingCardValueEditCampaign;
}

export interface EditCampaignData {
    id: string;
    exportId: string;
    type: CampaignType;
    serverValue: EditCampaignDataValue;
    value: EditCampaignDataValue;
    isStopped: boolean;
    hasNotModeratedBanners: boolean;
    areOtherSettingsVisible: boolean;
    availableForKeyGoals: Record<string, Goal>;
    isAvailableForKeyGoalsLoading: boolean;
    enabledFields: EnabledFieldsMap;
    relevantPhrasesGoals: {
        isMetrikaAvailable: boolean;
        goalsData: Record<string, Goal>;
    };
    errors: {
        general?: ValidationErrorData[];
        [EditCampaignValueField.bannersLanguage]?: ValidationErrorData[];
    };
}

export interface EditCampaignSessionValue {
    campaignsIds: Array<string>;
}

export interface EditCampaignsConstants {
    maxBannedDomainsCount: number;
    rateCorrectionsConstants: RatesCorrectionsConstantsData;
    allowedRateCorrectionsData: Record<CampaignType, Array<GdBidModifierType>>;
    maxMetrikaCountersNumberForTextCampaign: number;
    minRelevantPhrasesLimit: number;
    maxRelevantPhrasesLimit: number;
    defaultRelevantPhrasesAllGoalsId: number;
    maxAllowedPageIdsCount: number;
    maxDisabledIpsCount: number;
}

export interface EditCampaignsState {
    campaignsIds: string[];
    campaigns: Record<string, EditCampaignData>;
    globalError?: {
        title: string;
        hint: string;
    };
    isCreatePage: boolean;
    pageStatus: PageStatus;
    generalErrors: ValidationErrorData[];
    retargetingConditions?: Array<RetargetingConditionDataForRateCorrections>;
    constants?: EditCampaignsConstants;
}

export type CampaignsEditEditorsValidationResult = CampaignsEditEditorsValidationErrors & {
    campaignId: string;
};

export type CampaignsEditEditorsValidationErrors =
    | CampaignsEditCommonEditorValidationErrors
    | CampaignsEditRelevantPhrasesEditorValidationErrors
    | CampaignsEditNotificationsEditorValidationErrors
    | CampaignsEditRateCorrectionsEditorValidationErrors
    | CampaignsEditExperimentsEditorValidationErrors
    | CampaignsStrategyEditorValidationResult
    | CampaignMetrikaKeyGoalsEditorValidationResult;

export interface CampaignsEditCommonEditorValidationErrors {
    editor:
        | EditCampaignEditor.bannedDomainsEditor
        | EditCampaignEditor.disabledIpsEditor
        | EditCampaignEditor.nameEditor
        | EditCampaignEditor.allowedPageIdsEditor
        | EditCampaignEditor.metrikaCounterEditor
        | EditCampaignEditor.bannersLanguage
        | EditCampaignEditor.visitingCardEditor
        | 'general';
    errors: ValidationErrorData[];
}

export interface CampaignsEditRelevantPhrasesEditorValidationErrors {
    editor: EditCampaignEditor.relevantPhrasesEditor;
    errors: RelevantPhrasesErrors;
}

export interface CampaignsEditNotificationsEditorValidationErrors {
    editor: EditCampaignEditor.notificationsEditor;
    errors: NotificationsEditorValidationData;
}

export interface CampaignsEditRateCorrectionsEditorValidationErrors {
    editor: EditCampaignEditor.rateCorrectionsEditor;
    errors: Record<RateCorrectionType, RateCorrectionValidationResult>;
}

export interface CampaignsEditExperimentsEditorValidationErrors {
    editor: EditCampaignEditor.experimentsEditor;
    errors: ExperimentsEditorValidationData;
}

export interface CampaignsStrategyEditorValidationResult {
    editor: EditCampaignEditor.strategyEditor;
    errors: StrategyEditorValidationResult;
}

export interface CampaignMetrikaKeyGoalsEditorValidationResult {
    editor: EditCampaignEditor.metrikaKeyGoalsEditor;
    keyGoalsValidationData: MetrikaKeyGoalValidationData[];
}

export interface EditCampaignsErrorsData {
    campaigns: Record<string, EditCampaignValidationErrors>;
}

export type EditCampaignValidationErrors = {
    [EditCampaignValueField.name]?: BaseEditCampaignValidationErrorsType;
    [EditCampaignValueField.notifications]?: NotificationsEditCampaignValidationErrorsType;
    [EditCampaignValueField.metrikaCounters]?: BaseEditCampaignValidationErrorsType;
    [EditCampaignValueField.bannersLanguage]?: BaseEditCampaignValidationErrorsType;
    [EditCampaignValueField.visitingCard]: VisitingCardEditCampaignValidationErrorsType;
    common?: BaseEditCampaignValidationErrorsType;
};

export type BaseEditCampaignValidationErrorsType = ErrorsForValidationErrorsData;

export interface NotificationsEditCampaignValidationErrorsType {
    common: {
        email?: ErrorsForValidationErrorsData;
    };
}

export interface VisitingCardEditCampaignValidationErrorsType {
    vcardAddItems: {
        '0': {
            companyName: ErrorsForValidationErrorsData;
            ogrn: ErrorsForValidationErrorsData;
            email: ErrorsForValidationErrorsData;
            address: {
                country: ErrorsForValidationErrorsData;
                city: ErrorsForValidationErrorsData;
                street: ErrorsForValidationErrorsData;
                houseWithBuilding: ErrorsForValidationErrorsData;
                apartment: ErrorsForValidationErrorsData;
            };
            phone: {
                countryCode: ErrorsForValidationErrorsData;
                cityCode: ErrorsForValidationErrorsData;
                phoneNumber: ErrorsForValidationErrorsData;
                extension: ErrorsForValidationErrorsData;
            };
            instantMessenger: {
                login: ErrorsForValidationErrorsData;
            };
            extraMessage: ErrorsForValidationErrorsData;
        };
    };
}

export interface EditCampaignsConstants {
    involvedSessionsId: string;
    maxBannedDomainsCount: number;
    maxDisabledIpsCount: number;
    maxNameLength: number;
}

export interface FetchCampaignsGoalsAvailableForKeyGoalsInput {
    campaignId: string;
    counterIds: string[];
}

export type EnabledFieldsMap = Partial<Record<EditCampaignValueField, boolean>>;
